import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { interviewService } from '@/services'
import type { InterviewSession, InterviewMessage } from '@/types/api'
import { useInterviewStream } from '@/hooks/useInterviewStream'
import { useLegacyUpdatesStream } from '@/hooks/useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from '@/hooks/useLegacyUpdatesStream'
import { useInterviewStreamStore } from '@/stores/interviewStreamStore'
import { useChatSessionsStore } from '@/stores/chatSessionsStore'

/**
 * Encapsulates all chat-session logic:
 * session create/load, message send, SSE streams, and agent state.
 */
export function useChatSession() {
  const navigate = useNavigate()

  const [session, setSession] = useState<InterviewSession | null>(null)
  const [messages, setMessages] = useState<InterviewMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const creatingSessionRef = useRef(false)

  const { gaps, setGaps, setAgentState, agentState, reset: resetStream } = useInterviewStreamStore()
  const { addSession } = useChatSessionsStore()

  const getActiveGapId = useCallback((): string | undefined => {
    const { gaps } = useInterviewStreamStore.getState()
    return gaps.find((gap) => gap.status !== 'resolved')?.id
  }, [])

  // ── Auto-scroll on new messages ─────────────────────────────
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  // ── SSE: per-session interview stream ───────────────────────
  const handleStreamMessage = useCallback((msg: InterviewMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === msg.id)) return prev
      if (msg.clientId) {
        const idx = prev.findIndex((m) => m.metadata?.clientId === msg.clientId)
        if (idx !== -1) {
          const next = [...prev]
          next[idx] = { ...msg, metadata: { ...next[idx].metadata, pending: false, clientId: msg.clientId } }
          return next
        }
      }
      return [...prev, msg]
    })
  }, [])

  useInterviewStream({
    sessionId: session?.id,
    enabled: !!session,
    onMessage: handleStreamMessage,
    onMessageAck: useCallback(
      (payload: { clientId: string; messageId: string }) => {
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.metadata?.clientId === payload.clientId)
          if (idx === -1) return prev
          const next = [...prev]
          next[idx] = { ...next[idx], id: payload.messageId, metadata: { ...next[idx].metadata, pending: false } }
          return next
        })
      },
      []
    ),
    onAgentState: setAgentState,
    onContextPatch: useCallback(
      (payload: Record<string, unknown>) => {
        if (payload?.update) {
          const ctx: InterviewMessage = {
            id: `ctx-${Date.now()}`,
            role: 'assistant',
            content: String(payload.update),
            timestamp: new Date().toISOString(),
            metadata: { isContextUpdate: true },
          }
          setMessages((prev) => [...prev, ctx])
        }
      },
      []
    ),
    onGapUpdate: useCallback(
      (payload: Record<string, unknown>) => {
        if (payload?.gaps) setGaps(payload.gaps as never[])
      },
      [setGaps]
    ),
    onError: useCallback((event: Event) => console.error('SSE error:', event), []),
  })

  // ── SSE: unified updates stream for agent-state indicators ──
  useLegacyUpdatesStream({
    entityTypes: ['interview'],
    entityIds: session?.id ? [session.id] : undefined,
    enabled: !!session,
    onUpdate: useCallback(
      (event: LegacyUpdateEvent) => {
        if (event.eventType === 'interview.agent.thinking') setAgentState('thinking')
        else if (event.eventType === 'interview.agent.extracting') setAgentState('extracting')
        else if (event.eventType === 'interview.agent.idle') setAgentState('idle')
      },
      [setAgentState]
    ),
  })

  // ── Load existing session ───────────────────────────────────
  const loadSession = useCallback(
    async (id: string) => {
      // Skip if startSession is in progress — it already set the correct state
      // and the navigate() call that triggered this loadSession is expected.
      if (creatingSessionRef.current) return

      setLoading(true)
      resetStream()
      try {
        const [sessionData, history] = await Promise.all([
          interviewService.getSession(id),
          interviewService.legacyGetHistory(id),
        ])
        if (sessionData) {
          setSession(sessionData)
          setMessages(history.length > 0 ? history : sessionData.messages ?? [])
          setGaps(sessionData.identifiedGaps ?? [])
        }
      } catch (err) {
        console.error('Failed to load session:', err)
      } finally {
        setLoading(false)
      }
    },
    [resetStream, setGaps]
  )

  // ── Process REST response (fallback for SSE) ────────────────
  const processMessageResponse = useCallback(
    (
      assistantMsg?: { id: string; content: string; createdAt: string } | null,
      gapUpdates?: Record<string, 'resolved' | 'in_progress'> | null,
      sentClientId?: string
    ) => {
      if (sentClientId) {
        setMessages((prev) =>
          prev.map((m) =>
            m.metadata?.clientId === sentClientId ? { ...m, metadata: { ...m.metadata, pending: false } } : m
          )
        )
      }
      if (assistantMsg) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === assistantMsg.id)) return prev
          return [...prev, { id: assistantMsg.id, role: 'assistant' as const, content: assistantMsg.content, timestamp: assistantMsg.createdAt }]
        })
      }
      if (gapUpdates && Object.keys(gapUpdates).length > 0) {
        const { gaps } = useInterviewStreamStore.getState()
        const updated = gaps.map((g) => (gapUpdates[g.id] ? { ...g, status: gapUpdates[g.id] } : g))
        setGaps(updated as typeof gaps)
      }
    },
    [setGaps]
  )

  // ── Start a new session (optionally send first message) ─────
  const startSession = useCallback(
    async (firstMessage?: string) => {
      setLoading(true)
      resetStream()
      setMessages([])
      setSession(null)
      creatingSessionRef.current = true
      try {
        const res = await interviewService.startSession()
        const newSessionId = res.session?.id ?? res.sessionId
        if (!newSessionId) throw new Error('No sessionId in response')

        const newSession: InterviewSession = res.session ?? { id: newSessionId, uid: '', status: 'active', messages: [] }
        setSession(newSession)
        addSession(newSession)

        if (res.initialMessage) {
          setMessages([{ id: res.initialMessage.id, role: 'assistant', content: res.initialMessage.content, timestamp: res.initialMessage.createdAt }])
        }
        if (res.targetGaps?.length) setGaps(res.targetGaps)

        navigate(`/chat/${newSessionId}`, { replace: true })

        if (firstMessage?.trim()) {
          const clientId = `client-${Date.now()}`
          const pending: InterviewMessage = { id: clientId, role: 'user', content: firstMessage, timestamp: new Date().toISOString(), metadata: { pending: true, clientId } }
          setMessages((prev) => [...prev, pending])
          try {
            const initialActiveGapId =
              res.targetGaps?.find((gap) => gap.status !== 'resolved')?.id
            const msgRes = await interviewService.legacySendMessage(
              newSessionId,
              firstMessage,
              initialActiveGapId,
              new Date().toISOString()
            )
            processMessageResponse(msgRes.message, msgRes.gapUpdates, clientId)
          } catch (err) {
            console.error('Failed to send first message:', err)
            setMessages((prev) => prev.filter((m) => m.metadata?.clientId !== clientId))
          }
        }
      } catch (err) {
        console.error('Failed to start session:', err)
      } finally {
        setLoading(false)
        creatingSessionRef.current = false
      }
    },
    [navigate, resetStream, setGaps, addSession, processMessageResponse]
  )

  // ── Send a message in the current session ───────────────────
  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !session) return
      const clientId = `client-${Date.now()}`
      const pending: InterviewMessage = { id: clientId, role: 'user', content: text, timestamp: new Date().toISOString(), metadata: { pending: true, clientId } }
      setMessages((prev) => [...prev, pending])
      try {
        const res = await interviewService.legacySendMessage(
          session.id,
          text,
          getActiveGapId(),
          new Date().toISOString()
        )
        processMessageResponse(res.message, res.gapUpdates, clientId)
      } catch (err) {
        console.error('Failed to send message:', err)
        setMessages((prev) => prev.filter((m) => m.metadata?.clientId !== clientId))
      }
    },
    [session, processMessageResponse, getActiveGapId]
  )

  // ── Reset to a fresh state (new chat) ───────────────────────
  const resetChat = useCallback(() => {
    setSession(null)
    setMessages([])
    resetStream()
    setInputValue('')
    navigate('/chat', { replace: true })
  }, [navigate, resetStream])

  return {
    session,
    messages,
    gaps,
    inputValue,
    setInputValue,
    loading,
    agentState,
    scrollRef,
    loadSession,
    startSession,
    sendMessage,
    resetChat,
  }
}
